﻿import { LngLat, MapboxGeoJSONFeature, Map } from 'mapbox-gl';

import { ProjectionLike } from 'ol/proj';
import { createEmpty, extend, getArea, getCenter, isEmpty } from 'ol/extent';

import GeometryConverter from '../geometries/GeometryConverter';

type layerType =
    | 'fill'
    | 'line'
    | 'symbol'
    | 'circle'
    | 'fill-extrusion'
    | 'raster'
    | 'background'
    | 'heatmap'
    | 'hillshade';

const layerTypePriorities: layerType[][] = [
    ['symbol'],
    ['line'],
    ['fill', 'fill-extrusion', 'circle'],
    ['raster', 'heatmap', 'hillshade', 'background']
]

class MapBoxUtilities {

    /**
     * Simple geometries first
     * @param features
     * @param reverse complex geometries first if true
     */
    public static sortFeaturesByType(features: MapboxGeoJSONFeature[], reverse?: boolean) {

        features.sort((a, b) => {
            const atype = a.layer?.type;
            const btype = b.layer?.type
            var aIndex = -1;
            var bIndex = -1;

            if (atype !== btype) {
                layerTypePriorities.forEach((types, i) => {
                    if (atype && types.indexOf(atype as layerType) !== -1)
                        aIndex = i
                    if (btype && types.indexOf(btype as layerType) !== -1)
                        bIndex = i;
                })
            }

            return (aIndex - bIndex) * (reverse ? -1 : 1);
        })

        return features;
    }

    /**
     * Smallest first
     * @param features
     * @param reverse largest first if true
     */
    public static sortFeaturesBySize(features: MapboxGeoJSONFeature[], reverse?: boolean) {
        // note: use a-b for ascending order and b-a descending order
        features.sort((a, b) => {
            return (this.calculateAreaSize(a) - this.calculateAreaSize(b)) * (reverse ? -1 : 1);
        })
        return features;
    }

    /**
     * Closest first
     * @param features
     * @param ref
     * @param map
     * @param reverse closest last if true
     */
    public static sortFeaturesByDistance(features: MapboxGeoJSONFeature[], ref: LngLat, map: Map, reverse?: boolean) {
        features.sort((a, b) => {
            return (this.calculateSqrDistance(a, ref, map) - this.calculateSqrDistance(b, ref, map)) * (reverse ? -1 : 1);
        })
        return features;
    }

    // might be a better way to calculate the area...
    public static calculateAreaSize(feature: MapboxGeoJSONFeature) {
        const extent = this.caluculateExtent(feature, "EPSG:3857");
        return extent === undefined ? 0 : getArea(extent);
    }

    public static caluculateExtent(feature: MapboxGeoJSONFeature, featureProjection: ProjectionLike) {
        var extent = createEmpty();
        try {
            GeometryConverter.getOpenlayersFeatures(feature?.geometry, { featureProjection })?.forEach(f => {
                const geo = f.getGeometry();
                if (geo)
                    extent = extend(extent, geo.getExtent());
            })
        }
        catch (e) {
            console.error("Failed to read geometry", e);
        }
        return isEmpty(extent) ? undefined : extent;
    }

    public static calculateSqrDistance(feature: MapboxGeoJSONFeature, coordinate: LngLat, map: Map) {
        const ext = this.caluculateExtent(feature, 'EPSG:4326')
        if (ext !== undefined) {
            const [lon, lat] = getCenter(ext);
            return map.project({ lat, lon }).distSqr(map.project(coordinate));
        }
        return Number.MAX_SAFE_INTEGER;
    }
}

export default MapBoxUtilities
